import React, { Fragment, useState } from "react";
import { Button } from "semantic-ui-react";
import { Form, Format, ModalBuilder, TriggerBuilder } from "@redriver/cinnamon";
import { TranslatedModal } from "features/../../../shared/components/modals";
import { useTranslation } from "react-i18next";

import { connect } from "react-redux";
import {
  calculateFunderSheet,
  DownloadConfirmationModal,
  downloadSheetFunderVersion,
} from "features/Sheets/ManageSheet";
import { FormattedNumericInput } from "features/../../../shared/components/forms";

const LabelValueRow = ({ label, value, boldLabel, boldValue }) => {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span
        style={{
          fontWeight: boldLabel ? "bold" : "normal",
          marginRight: "10px",
          minWidth: "50%",
        }}
      >
        {label}
      </span>
      <span
        style={{
          textAlign: "left",
          flexGrow: 1,
          minWidth: "50%",
          fontWeight: boldValue ? "bold" : "normal",
        }}
      >
        {value}
      </span>
    </div>
  );
};

const FunderSheetInformationModal = ({
  sheetId,
  sheetData,
  disableTrigger,
  disableSubmit,
  onSubmitted,
  renderTrigger,
  saveBeforeDownload,
}) => {
  const currencyFormat = "GBP";
  const [calcData, setCalcData] = useState({});
  const [calculated, setCalculated] = useState(false);
  const { t } = useTranslation("sheetTranslation"),
    tScope = "printFunderSheet";

  const { t: tSharedButtons } = useTranslation("shared", {
    keyPrefix: "buttons",
  });

  const ArrayRows = ({ label, list }) => {
    const arr = Array.isArray(list) ? list : list == null ? [] : [list];
    if (arr.length === 0) {
      return (
        <LabelValueRow
          key={`${label}-empty`}
          label={label}
          value={<Format.Number value={0} format={currencyFormat} />}
        />
      );
    }
    return (
      <>
        {arr.map((val, idx) => (
          <LabelValueRow
            key={`${label}-${idx}`}
            label={idx === 0 ? label : ""}
            value={<Format.Number value={val ?? 0} format={currencyFormat} />}
          />
        ))}
      </>
    );
  };

  return (
    <ModalBuilder
      withForm
      loadAction={calculateFunderSheet}
      loadParams={{
        sheetId: sheetId,
        sheetData: sheetData,
      }}
      onSubmitted={onSubmitted}
      renderTrigger={
        renderTrigger ||
        ((onRender) => (
          <Button
            primary
            compact
            icon="print"
            disabled={disableTrigger}
            onClick={onRender}
            content={t("printFunderSheet.trigger")}
          />
        ))
      }
      renderModal={(modalProps, formProps, state, events) => (
        <TranslatedModal.Edit
          {...modalProps}
          size="large"
          header={t(`${tScope}.header`)}
          submitDisabled={state.loading || state.error || disableSubmit}
          className="print-funder-modal custom-actions"
        >
          <Form {...formProps}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                marginTop: "2px",
                marginBottom: "50px",
              }}
            >
              <LabelValueRow
                key="customer"
                label="Customer"
                value={formProps.value.clientName}
                boldLabel
                boldValue
              />
              <LabelValueRow
                key="sheetName"
                label="Sheet Name"
                value={formProps.value.sheetName}
                boldLabel
                boldValue
              />
              <LabelValueRow
                key="financeSelection"
                label="Finance Selection"
                value={formProps.value.financeSelection ?? 0}
              />
              <LabelValueRow
                key="grossSalesRate"
                label="Gross Sales Rate"
                value={formProps.value.grossSalesRate ?? 0}
              />
              <LabelValueRow
                key="ipsNetRate"
                label="Net Rate"
                value={
                  <FormattedNumericInput
                    field="ipsNetRate"
                    width={8}
                    decimalPlaces={{ min: 0, max: 3 }}
                  />
                }
              />
              <LabelValueRow
                key="capitalCost"
                label="Capital Cost"
                value={
                  <Format.Number
                    value={
                      calcData.capitalCosts ?? formProps.value.capitalCost ?? 0
                    }
                    format={currencyFormat}
                  />
                }
              />
              <ArrayRows
                label="Settlement"
                list={formProps.value.settlements}
              />
              <ArrayRows label="Upgrade" list={formProps.value.upgrades} />
              <LabelValueRow
                key="liquidityPercentage"
                label="Liquidity %"
                value={`${
                  calcData?.liquidityPercentage ??
                  formProps.value.liquidityPercentage ??
                  0
                }%`}
              />
              <LabelValueRow
                key="totalCapitalInvestment"
                label="Total Capital Investment (incl Rounding)"
                value={
                  <Format.Number
                    value={formProps.value.totalCapitalInvestment ?? 0}
                    format={currencyFormat}
                  />
                }
              />
              <LabelValueRow
                key="liquidityChargeDisclosed"
                label="Liquidity Charge disclosed on doc"
                value={
                  <Format.Number
                    value={formProps.value.liquidityChargeDisclosed ?? 0}
                    format={currencyFormat}
                  />
                }
              />
              <LabelValueRow
                key="additionalLiquidityCharge"
                label="Additional Liquidity Charge"
                value={
                  <Format.Number
                    value={
                      calcData?.additionalLiquidityCharge ??
                      formProps.value.additionalLiquidityCharge ??
                      0
                    }
                    format={currencyFormat}
                  />
                }
              />
              <LabelValueRow
                key="totalInvoiceValue"
                label="Total Invoice Value"
                value={
                  <Format.Number
                    value={
                      calcData?.totalInvoiceValue ??
                      formProps.value.totalInvoiceValue ??
                      0
                    }
                    format={currencyFormat}
                  />
                }
                boldLabel={true}
                boldValue={true}
              />
              <hr style={{ margin: "16px 0", border: "1px solid #ccc" }} />
              <Form>
                <LabelValueRow
                  key="newValuesAfterAmendments"
                  label="New Values After Amendments"
                  boldLabel={true}
                />
                <LabelValueRow
                  key="newCapitalInvestment"
                  label="New Capital Investment"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newCapitalInvestment}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="newLiquidityCharge"
                  label="New Liquidity Charge"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newLiquidityCharge}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="newTotalInvoiceValue"
                  label="New Total Invoice Value"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newTotalInvoiceValue}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                  boldLabel
                  boldValue
                />
                <hr style={{ margin: "16px 0", border: "1px solid #ccc" }} />
                <LabelValueRow
                  key="newLiquidityPercentage"
                  label="New Liquidity Percentage"
                  value={
                    calculated ? (
                      <Fragment>
                        <Format.Number
                          value={calcData?.newLiquidityPercentage}
                        />
                        %
                      </Fragment>
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <hr style={{ margin: "16px 0", border: "1px solid #ccc" }} />
                <LabelValueRow
                  key="newGrossSaleRate"
                  label="Gross Sale Rate to Be Used"
                  value={
                    calculated ? (
                      <Format.Number value={calcData?.newGrossSaleRate} />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="newNetRateToBeUsed"
                  label="Net Rate to Be Used"
                  value={
                    calculated ? (
                      <Format.Number value={calcData?.ipsNetRateOverride} />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <hr style={{ margin: "16px 0", border: "1px solid #ccc" }} />
                <LabelValueRow
                  key="newCalculatedCfaPayment"
                  label="Calculated CFA Payment"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newCalculatedCfaPayment}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="newFunderCfaPaymentPerSheet"
                  label="Funder CFA Payment (per sheet)"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.funderCfaPaymentPerSheet}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="cfaPaymentCheck"
                  label="CFA Payment Check"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newCfaPaymentCheck}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <hr style={{ margin: "16px 0", border: "1px solid #ccc" }} />
                <LabelValueRow
                  key="newCapitalValueRecalculated"
                  label="Capital Value Recalculated"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newCapitalValueRecalculated}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
                <LabelValueRow
                  key="newCapitalValueCheck"
                  label="Capital Value Check"
                  value={
                    calculated ? (
                      <Format.Number
                        value={calcData?.newCapitalValueCheck}
                        format={currencyFormat}
                      />
                    ) : (
                      "not calculated"
                    )
                  }
                />
              </Form>
            </div>
            <div
              className="custom-action-container"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Button
                negative
                content={tSharedButtons("cancel")}
                onClick={events.onCancel}
              />
              <TriggerBuilder
                submitAction={calculateFunderSheet}
                submitParams={{
                  sheetId: sheetId,
                  sheetData: sheetData,
                  ipsNetRateOverride: formProps.value.ipsNetRate,
                }}
                onSubmitted={(data) => {
                  setCalcData(data);
                  setCalculated(true);
                }}
                renderTrigger={(fireTrigger, state) => (
                  <Button
                    disabled={
                      state.submitting ||
                      state.loading ||
                      !formProps?.value?.ipsNetRate
                    }
                    loading={state.submitting}
                    primary
                    onClick={fireTrigger}
                    className="calculate-button"
                  >
                    <div>{t("printFunderSheet.calculateLabel")}</div>
                  </Button>
                )}
              />
              <DownloadConfirmationModal
                sheetId={sheetId}
                sheetDownloadFunction={downloadSheetFunderVersion}
                saveBeforeDownload={saveBeforeDownload}
                sheetDownloadParams={{
                  ipsNetRateOverride: calcData?.ipsNetRateOverride,
                }}
                buttonContent="Print"
                buttonClassName="green"
                disabled={
                  state.submitting ||
                  state.loading ||
                  calcData.ipsNetRateOverride == null
                }
              />
            </div>
          </Form>
        </TranslatedModal.Edit>
      )}
    />
  );
};

export default connect()(FunderSheetInformationModal);
